import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

export interface TTSConfig {
  voice?: string;
  volume?: number;
  rate?: number;
  pitch?: number;
}

export interface TTSState {
  isPlaying: boolean;
  isSpeaking: boolean;
  isIgnoreUntilNextDialog?: boolean;
  currentText: string;
  queueLength: number;
  volume: number;
}

@Injectable({
  providedIn: 'root'
})
export class TextToSpeechService {
  private audioContext: AudioContext;
  private audioQueue: AudioBuffer[] = [];
  private currentSource: AudioBufferSourceNode | null = null;

  // State management
  private stateSubject = new BehaviorSubject<TTSState>({
    isPlaying: false,
    isSpeaking: false,
    isIgnoreUntilNextDialog: false,
    currentText: '',
    queueLength: 0,
    volume: 1
  });

  // Cancellation and control
  private stopSubject = new Subject<void>();
  private pauseSubject = new Subject<void>();
  private resumeSubject = new Subject<void>();

  // Expose state as observable
  public state$ = this.stateSubject.asObservable();

  constructor(private http: HttpClient) {
    this.audioContext = new AudioContext();
  }

  public listDevices(){
    if (!navigator.mediaDevices?.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
    } else {
      // List cameras and microphones.
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          devices.forEach((device) => {
            console.log(`${device.kind}: ${device.label} id = ${device.deviceId}`);
          });
        })
        .catch((err) => {
          console.error(`${err.name}: ${err.message}`);
        });
    }
  }


  public clearQueue(): void {
    // Clear audio queue
    this.audioQueue = [];
    // Update state
    this.updateState({
      isSpeaking: false,
      queueLength: this.audioQueue.length
    });

    this.audioContext = new AudioContext();
  }


  // Public method to stop all audio and streaming
  public stop(): void {

    // if (this.audioContext.state === 'running') {
    //   this.audioContext.suspend();
    // }

    // Stop the current audio source
    if (this.currentSource) {
      this.currentSource.stop();
      this.currentSource.disconnect();
      this.currentSource = null;
    }

    // Clear audio queue
    this.audioQueue = [];

    // Emit stop signal to cancel streaming
    this.stopSubject.next();
    this.stopSubject.complete();

    // Recreate stop subject for future use
    this.stopSubject = new Subject<void>();

    // Reset state
    this.resetState();

    console.log("hit end of stop oftxttospeech");
  }

  // Pause current audio playback
  public pause(): void {
    if (this.audioContext.state === 'running') {
      this.audioContext.suspend();
      this.updateState({ isPlaying: false });
      this.pauseSubject.next();
    }
  }

  // Resume audio playback
  public resume(): void {
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
      this.updateState({ isPlaying: true });
      this.resumeSubject.next();
    }
  }

  // Set volume (0-1 range)
  public setVolume(volume: number): void {
    // Ensure volume is between 0 and 1
    const safeVolume = Math.max(0, Math.min(1, volume));

    // Update gain if audio context exists
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(safeVolume, this.audioContext.currentTime);

    this.updateState({ volume: safeVolume });
  }

  // Get current service state
  public getState(): TTSState {
    return this.stateSubject.getValue();
  }

  // Private method to update internal state
  public updateState(updates: Partial<TTSState>): void {
    const currentState = this.stateSubject.getValue();
    this.stateSubject.next({
      ...currentState,
      ...updates,
      queueLength: this.audioQueue.length
    });
  }


  // Reset state to initial conditions
  public resetState(): void {
    // Clear audio queue
    this.audioQueue = [];

    this.updateState({
      isPlaying: false,
      isSpeaking: false,
      isIgnoreUntilNextDialog: false,
      currentText: '',
      queueLength: 0
    });
    this.audioContext.close
    //this.audioContext = new AudioContext();
  }


  // Modify playback to support configuration
  public async playPollyAudio(base64Audio: string, config: TTSConfig = {}) {
    try {
      if(!base64Audio)
        return;

      // Decode base64 audio
      const audioBuffer = await this.decodeAudioBuffer(base64Audio);

      // Add to queue
      this.audioQueue.push(audioBuffer);

      // Update state
      this.updateState({
        isSpeaking: true,
        queueLength: this.audioQueue.length
      });

      // Start playback if not already playing
      if(!this.getState().isPlaying){
        this.playNextInQueue(config);
      }
    } catch (error) {
      console.error('Audio playback error:', error, "AudioBase64: " + base64Audio);
      this.updateState({ isSpeaking: false });
    }
  }


  private async playNextInQueue(config: TTSConfig) {
    if (this.audioQueue.length === 0) {
      this.updateState({
        isPlaying: false,
        isSpeaking: false
      });
      return;
    }

    this.updateState({ isPlaying: true });
    const audioBuffer = this.audioQueue.shift()!;

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioContext.destination);
    //this.currentSource = source;

    // Apply rate and pitch if specified
    if (config.rate) {
      source.playbackRate.value = config.rate;
    }

    // Connect to audio context
    // const gainNode = this.audioContext.createGain();
    // gainNode.gain.setValueAtTime(config.volume || 1, this.audioContext.currentTime);

    // source.connect(gainNode);
    // gainNode.connect(this.audioContext.destination);

    source.onended = () => {
      this.playNextInQueue(config);
    };

    source.start(0);
  }


  // Existing decodeAudioBuffer method remains the same
  private async decodeAudioBuffer(base64Audio: string): Promise<AudioBuffer> {
    const audioData = atob(base64Audio);
    const audioArrayBuffer = new ArrayBuffer(audioData.length);
    const view = new Uint8Array(audioArrayBuffer);

    for (let i = 0; i < audioData.length; i++) {
      view[i] = audioData.charCodeAt(i);
    }

    return await this.audioContext.decodeAudioData(audioArrayBuffer);
  }


} //end class
